import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, X } from "lucide-react";
import { api } from "../lib/api";

export function FrameworksList() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/frameworks").then(r => setItems(r.data.frameworks));
  }, []);

  const categories = useMemo(() => Array.from(new Set(items.map(i => i.category))).sort(), [items]);

  const filtered = items.filter(i =>
    (filter === "all" || i.category === filter) &&
    (q === "" || i.name.toLowerCase().includes(q.toLowerCase()) || i.tagline.toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div data-testid="page-frameworks">
      <section className="border-b border-slate-200">
        <div className="container-rp py-16 lg:py-24">
          <div className="overline">Risk Frameworks</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 tracking-tight">The Encyclopedia.</h1>
          <p className="text-slate-600 mt-4 max-w-2xl text-lg">32+ risk frameworks indexed with complexity, certification status and time-to-implement — searchable, filterable, comparable.</p>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-slate-50">
        <div className="container-rp py-5 flex flex-wrap items-center gap-3">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search frameworks..." className="flex-1 min-w-[200px] border border-slate-200 rounded-sm px-4 py-2 text-sm bg-white" data-testid="framework-search" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setFilter("all")} data-active={filter === "all"} className="persona-btn" data-testid="filter-all">All</button>
            {categories.map(c => (
              <button key={c} onClick={() => setFilter(c)} data-active={filter === c} className="persona-btn" data-testid={`filter-${c.toLowerCase()}`}>{c}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-rp">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {filtered.map(f => (
              <Link to={`/frameworks/${f.slug}`} key={f.slug} className="bg-white p-6 hover:bg-slate-50 transition-colors flex flex-col" data-testid={`framework-card-${f.slug}`}>
                <div className="overline-muted">{f.category}</div>
                <div className="font-display text-xl font-semibold mt-2">{f.name}</div>
                <div className="text-sm text-slate-500 mt-1">{f.tagline}</div>
                <p className="text-sm text-slate-600 mt-4 leading-relaxed line-clamp-3 flex-1">{f.description}</p>
                <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4 text-xs">
                  <span className="font-mono text-slate-500">{f.complexity} · {f.time_to_implement}</span>
                  {f.certification ? <span className="text-emerald-600 font-medium">Cert. track</span> : <span className="text-slate-400">No cert</span>}
                </div>
              </Link>
            ))}
          </div>
          {filtered.length === 0 && <div className="text-center py-16 text-slate-400 text-sm">No frameworks match your filter.</div>}
        </div>
      </section>
    </div>
  );
}

export function FrameworkDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [fw, setFw] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/frameworks/${slug}`).then(r => setFw(r.data)).catch(() => setError(true));
  }, [slug]);

  if (error) return <NotFound title="Framework not found" />;
  if (!fw) return <div className="container-rp py-24 text-slate-400">Loading…</div>;

  return (
    <div data-testid={`page-framework-${fw.slug}`}>
      <section className="border-b border-slate-200">
        <div className="container-rp py-12">
          <button onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-6" data-testid="framework-back">
            <ArrowLeft size={12} /> Back
          </button>
          <div className="overline">{fw.category}</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 tracking-tight">{fw.name}</h1>
          <p className="text-slate-600 mt-3 text-xl">{fw.tagline}</p>
        </div>
      </section>

      <section className="section">
        <div className="container-rp grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <p className="text-lg text-slate-700 leading-relaxed">{fw.description}</p>

            <div className="mt-12">
              <div className="overline">Singapore application</div>
              <h2 className="font-display text-3xl font-semibold mt-3">How this maps to Singapore BFSI</h2>
              <p className="text-slate-600 mt-4 leading-relaxed">
                {fw.category === "Singapore"
                  ? `This is a Singapore-native rulebook. It directly governs MAS-regulated entities and other in-scope organisations operating in Singapore.`
                  : `${fw.name} is widely referenced in MAS TRM guidance and is commonly mapped to ISO 27001 and PDPA controls during regulatory examinations. Adopting it strengthens evidence packs across MAS, PDPC and CSA reviews.`}
              </p>
            </div>

            <div className="mt-10 rp-card p-6 bg-slate-50">
              <div className="overline">Educational disclaimer</div>
              <p className="text-sm text-slate-600 mt-2">Content is for educational purposes only. It is not legal, regulatory or investment advice. Refer to the issuing authority's official publications for binding text.</p>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="rp-card p-6 sticky top-24">
              <div className="overline">At a glance</div>
              <dl className="mt-4 space-y-3 text-sm">
                <Row label="Category" value={fw.category} />
                <Row label="Complexity" value={fw.complexity} />
                <Row label="Time to implement" value={fw.time_to_implement} />
                <Row label="Certification" value={fw.certification ? <span className="text-emerald-600 inline-flex items-center gap-1"><Check size={12} /> Yes</span> : <span className="text-slate-400 inline-flex items-center gap-1"><X size={12} /> No</span>} />
              </dl>
              <Link to="/tools/framework-matcher" className="btn-accent w-full justify-center mt-6">Find your best match</Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
      <dt className="text-slate-500 text-xs uppercase tracking-wider">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

export function NotFound({ title = "Page not found" }) {
  return (
    <div className="container-rp section text-center">
      <div className="overline">404</div>
      <h1 className="font-display text-5xl font-bold mt-3">{title}</h1>
      <p className="text-slate-600 mt-4">Looks like that page hasn't been built yet.</p>
      <Link to="/" className="btn-primary mt-8 inline-flex">Back to home <ArrowRight size={14} /></Link>
    </div>
  );
}
